import { useId, useMemo } from 'react';

import {
  Badge,
  DataTableSelectionCheckbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from 'design-system';

import { DateTextVariants, useDateText, useUserDetails } from '@/features/_shared/user';
import { FactsheetDialogButton } from '@/features/factsheet';
import { useSelectionFunctions } from '@/features/symbol-selector';
import { isValidDateString } from '@/shared/lib/date/isValidDateString';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';

import { getMiniTableSymbols } from '../../lib/getMiniTableSymbols';

import type { MiniTableRow, MiniTableType } from '../../../../types';

function roundHalfUp(num: number, decimals = 0) {
  const factor = Math.pow(10, decimals);
  return (Math.sign(num) * Math.round(Math.abs(num) * factor)) / factor;
}

function formatNumberInBraces(value: string) {
  return value.replaceAll(/\{([^{}]*)}/g, (_, content) => {
    const result = content.trim();
    if (!result) return '';
    if (Number.isNaN(Number(result))) return result;

    try {
      const decimalsToRound = 2;
      return formatNumberWithPrecision(
        roundHalfUp(Number(result), decimalsToRound).toFixed(decimalsToRound)
      );
    } catch {
      return result;
    }
  });
}

function splitRows(table: MiniTableType): {
  header: MiniTableRow | undefined;
  body: MiniTableRow[];
} {
  const firstRow = table.rows[0];
  if (firstRow && 'symbol' in firstRow) {
    return { header: undefined, body: table.rows };
  }

  return {
    header: firstRow,
    body: table.rows.slice(1)
  };
}

function getKeyedCells(cells: string[]) {
  const occurrences = new Map<string, number>();
  return cells.map(cell => {
    const occurrence = occurrences.get(cell) ?? 0;
    occurrences.set(cell, occurrence + 1);
    return { key: `${cell || 'empty'}-${occurrence}`, value: cell };
  });
}

function getKeyedRows(rows: MiniTableRow[]) {
  const occurrences = new Map<string, number>();
  return rows.map(row => {
    const signature = JSON.stringify(row);
    const occurrence = occurrences.get(signature) ?? 0;
    occurrences.set(signature, occurrence + 1);
    return { key: `${signature}-${occurrence}`, value: row };
  });
}

const MiniTable = ({ table }: { table: MiniTableType }) => {
  const titleId = useId();
  const { formatDateStringInBraces } = useDateText();
  const { isUserExpired } = useUserDetails();
  const { addSymbolsToSelection, areAllSymbolsSelected, removeSymbolsFromSelection, isSpotLocked } =
    useSelectionFunctions();

  const symbols = useMemo(() => getMiniTableSymbols(table), [table]);
  const isSymbolsSelected = symbols.length > 0 && areAllSymbolsSelected(symbols);
  const { header, body } = useMemo(() => splitRows(table), [table]);
  const columnCount = Math.max(header?.cells.length ?? 0, ...body.map(row => row.cells.length), 1);
  const factsheetCodes = useMemo(() => symbols.map(({ Code }) => Code).join(','), [symbols]);

  const toggleSelection = () => {
    if (isSpotLocked) {
      addSymbolsToSelection(symbols);
      return;
    }

    if (isSymbolsSelected) {
      removeSymbolsFromSelection(symbols);
    } else {
      addSymbolsToSelection(symbols);
    }
  };

  return (
    <section aria-labelledby={titleId}>
      <header className='flex h-8 items-center gap-2 px-2'>
        <DataTableSelectionCheckbox
          aria-label={`Select symbols in ${table.name}`}
          snapshot={{
            checked: isSymbolsSelected,
            indeterminate: false,
            disabled: isUserExpired || symbols.length === 0,
            locked: isSpotLocked
          }}
          onToggle={toggleSelection}
        />
        <h2
          id={titleId}
          className='min-w-0 flex-1 truncate text-sm font-bold'
        >
          {table.name}
        </h2>
        <FactsheetDialogButton code={factsheetCodes} />
      </header>

      <Table aria-label={`${table.name} table`}>
        {header ? (
          <TableHeader>
            <TableRow>
              {getKeyedCells(header.cells).map(({ key, value }, index) => (
                <TableHead
                  key={key}
                  className={index === 0 ? 'w-2/5' : 'w-3/10 text-right'}
                >
                  {value}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        ) : null}
        <TableBody>
          {getKeyedRows(body).map(({ key: rowKey, value: row }) => {
            const isHighlighted = row.layout?.highlight === 'true';

            return (
              <TableRow
                key={rowKey}
                data-state={isHighlighted ? 'selected' : undefined}
              >
                {getKeyedCells(row.cells).map(({ key, value: cell }, cellIndex) => {
                  const isFirstCell = cellIndex === 0;
                  const trimmedCell = cell.slice(1, -1);
                  const isDate =
                    Number.isNaN(Number(trimmedCell)) && isValidDateString(trimmedCell);
                  const isEmptyRow = cell === '' && row.cells.length === 1;
                  let content = formatNumberInBraces(cell);
                  if (cell === '') {
                    content = '';
                  } else if (isDate) {
                    content = formatDateStringInBraces(cell, DateTextVariants.MONTH_YEAR);
                  }
                  const metadata = isFirstCell ? Object.entries(row.metadata ?? {}) : [];

                  return (
                    <TableCell
                      key={key}
                      className={isFirstCell ? 'w-2/5' : 'w-3/10 text-right'}
                      colSpan={isEmptyRow ? columnCount : 1}
                    >
                      {content}
                      {metadata.length > 0 ? (
                        <span className='ml-2 inline-flex flex-wrap gap-1'>
                          {metadata.map(([label, value]) => (
                            <Badge
                              key={`${label}-${value}`}
                              aria-label={`${label}: ${value}`}
                              variant='muted'
                            >
                              {value}
                            </Badge>
                          ))}
                        </span>
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </section>
  );
};

export default MiniTable;
